// Foundations
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

// rxjs
import { filter, take, finalize, map } from 'rxjs/operators';

// Services
import { AuthService } from 'src/app/auth/auth.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { Account, GetCommPrefGQL, UpdateAccountGQL } from 'src/app/services/graphql.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';

@Component({
  selector: 'app-preferences',
  templateUrl: './preferences.component.html',
  styleUrls: ['./preferences.component.scss'],
})
export class PreferencesComponent implements OnInit {
  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private loadingIndicatorService: LoadingIndicatorService,
    private getCommPrefQuery: GetCommPrefGQL,
    private updateAccountMutation: UpdateAccountGQL,
    private _analytics: AnalyticsService
  ) {
    this.form = this.fb.group({
      email: [null, Validators.required],
      text: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this._analytics.logScreen('communicationPreferencesPage');

    this.fetchData();
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  onSave() {
    this._analytics.logButtonTap('communicationPreferences_save');

    const { email, text } = this.form.value;

    this.loadingIndicatorService.show();
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.updateAccountMutation
          .mutate({
            accountId,
            accountInput: {
              marketingEmailOptIn: email,
              marketingSmsOptIn: text,
            },
            forRegistration: false
          })
          .pipe(finalize(() => this.loadingIndicatorService.hide()))
          .subscribe((res) => {
            if (res.data) {
              this.snackBar.open('Preferences successfully updated.', '', {
                duration: 2000,
                panelClass: ['snackBar'],
              });
              this.router.navigate(['/']);
            }
          });
      });
  }

  // Private methods
  // ------------------------------------------------------------------------------------

  private fetchData() {
    this.loadingIndicatorService.show();
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.getCommPrefQuery
          .fetch({ accountId })
          .pipe(
            map((res) => res.data.getAccount),
            finalize(() => this.loadingIndicatorService.hide())
          )
          .subscribe((account) => {
            this.populateForms(account);
          });
      });
  }

  private populateForms(account: Partial<Account>) {
    const { marketingEmailOptIn, marketingSmsOptIn } = account;

    this.form.patchValue({
      email: marketingEmailOptIn,
      text: marketingSmsOptIn,
    });
  }
}
